import { gql, GraphQLClient } from "graphql-request";
import useSWR, { SWRConfig } from "swr";
import { useMemo, useCallback } from "react";
import { notifyDataChanges, useDataChangeNotifier } from "@/hooks/useDataChangeNotifier";
import { getStoredJWTToken } from "../AccountContext";



/**
 * Fetch wrapper for POST requests that includes JWT authentication.
 * @param url Endpoint URL (can be relative or absolute)
 * @param body Request body data
 * @returns Promise with the response data
 */
export async function postWithAuth<T = any>(url: string, body: any, sendErrorsAsSuccess = false): Promise<T> {
    const token = getStoredJWTToken();
    
    const headers: HeadersInit = {
        'Content-Type': 'application/json',
    };
    
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
    });
    console.log(`POST ${url} response was <${response.ok} | ${response.status}>:`, response);
    
    if (!response.ok) {
        if (sendErrorsAsSuccess) {
            try {
                // GraphQL should send a `{error:[...]}` type response.
                const ret = await response.json();
                console.log(`Parsed error response as JSON:`, ret);
                return ret;
            } catch (err) {
                // not critical error, just not a json successful-tyype response.
                console.log(`Error parsing error response as JSON: ${err}`);
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
}



export class GraphQLError extends Error {
    constructor(public readonly errors: Array<{ message: string }>) {
        super(errors.map(e => e.message).join(', '));
        this.name = 'GraphQLError';
    }
}

/**
 * Generic helper to execute GraphQL mutations with authentication.
 * @param mutation GraphQL mutation string
 * @param variables Variables for the mutation
 * @returns Promise with the mutation result data
 */
export async function postGraphqlMutation<T = any>(
    mutation: string,
    variables?: { [id: string]: any }
): Promise<T> {
    const baseUrl = typeof window !== 'undefined' 
        ? `${window.location.protocol}//${window.location.host}` 
        : '';
    const endpoint = `${baseUrl}/api/graphql`;
    
    const response = await postWithAuth<{ data: T; errors?: Array<{ message: string }> }>(
        endpoint,
        { query: mutation, variables }
    );
    
    if (response.errors) {
        throw new GraphQLError(response.errors);
    }
    
    return response.data;
}